import { memo, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { type SlateToolbarControl } from '@m/alchemy-ui/SlateRTE';
import { TranslatableRTE } from '../../../challenge-management/components';
import { type PanelButtonConfig } from '../../common/button-link-config';
import { PAGE_OBJECT_TYPE } from '../../common/const';
import { type PanelConfigPatch } from '../../common/panel-grid';
import { type PageCmsPanel } from '../../common/panel-types';
import { PanelEditorStretchSection } from '../../CustomPage.styled';
import { LinkConfigSelect } from './LinkConfigSelect';

const messages = defineMessages({
  textPlaceholder: {
    id: 'editCustomPageTextPlaceholder',
    defaultMessage: 'Enter your text or HTML'
  }
});

// Legacy toolbar minus the controls SlateRTE doesn't expose (clearFormatting, source).
const TEXT_EDITOR_CONFIG: { buttons: SlateToolbarControl[] } = {
  buttons: [
    'bold',
    'italic',
    'typeStyle',
    'unorderedList',
    'orderedList',
    'undo',
    'redo',
    'link',
    'alignLeft',
    'alignCenter',
    'alignRight'
  ]
};

type TextConfig = Extract<PageCmsPanel['config'], { __typename: 'TextPanelConfig' }>;

interface TextPanelEditorProps {
  readonly config: TextConfig;
  readonly panelId: string;
  readonly showPanelErrors: boolean;
  readonly onConfigChange: (patch: PanelConfigPatch) => void;
}

const TextPanelEditorComponent = ({ config, panelId, showPanelErrors, onConfigChange }: TextPanelEditorProps) => {
  const intl = useIntl();
  const translateField = `paneltext--${panelId}`;

  // The RTE reports its value on mount too — only patch real changes.
  const handleTextChange = useCallback(
    (text: string) => {
      if (text !== (config.text ?? '')) {
        onConfigChange({ text });
      }
    },
    [config.text, onConfigChange]
  );

  const handleButtonConfigChange = useCallback(
    (buttonConfig: PanelButtonConfig) => onConfigChange({ buttonConfig }),
    [onConfigChange]
  );

  return (
    <>
      <PanelEditorStretchSection>
        <TranslatableRTE
          labelId={`text-panel-editor-${panelId}`}
          type={PAGE_OBJECT_TYPE}
          field={translateField}
          translateId={translateField}
          config={TEXT_EDITOR_CONFIG}
          placeholder={intl.formatMessage(messages.textPlaceholder)}
          defaultLanguageValue={config.text ?? ''}
          onDefaultLanguageChange={handleTextChange}
        />
      </PanelEditorStretchSection>
      <LinkConfigSelect
        panelId={panelId}
        variant="button"
        buttonConfig={config.buttonConfig ?? null}
        showPanelErrors={showPanelErrors}
        onChange={handleButtonConfigChange}
      />
    </>
  );
};

// Immutable config updates make prop comparison cheap: the editor re-renders only when its
// own config (or the grid callback) changes.
export const TextPanelEditor = memo(TextPanelEditorComponent);
